/* eslint-disable @typescript-eslint/no-empty-function */
import { useCallback, useMemo, useState } from 'react';
import { Row } from './Datepicker.styles';
import { Day } from './Day';
import { DayOfWeek } from './DayOfWeek';
import {
  createNewDateRange,
  DateRange,
  getDayName,
  getWeeks,
  isDateAfter,
  isDateBefore,
  isDateEnd,
  isDateInRange,
  isDateSelected,
  isDateStart,
  isSameDate,
  DAYS_OF_WEEK
} from './utilities';

export interface MonthProps {
  month: number;
  year: number;
  rangeEnabled?: boolean;
  selected?: DateRange;
  disableDatesBefore?: Date;
  disableDatesAfter?: Date;
  disableSpecificDates?: Date[];
  onChange?: (date: DateRange) => void;
}

export const Month = ({
  month,
  year,
  rangeEnabled,
  selected,
  disableDatesBefore,
  disableDatesAfter,
  disableSpecificDates,
  onChange = (_date: DateRange) => {
    /* no-op default */
  }
}: MonthProps) => {
  const [hoverDate, setHoverDate] = useState<Date | null>(null);

  const weeks = useMemo(() => getWeeks(month, year), [month, year]);

  const daysOfWeek = useMemo(
    () => DAYS_OF_WEEK.map(dow => <DayOfWeek key={dow} label={getDayName(dow)} />),
    []
  );

  const dayClickHandler = useCallback(
    (selectedDate: Date) => {
      onChange(createNewDateRange(rangeEnabled ? selected : undefined, selectedDate));
    },
    [rangeEnabled, selected, onChange]
  );

  const hoverHandler = useCallback((date: Date) => {
    setHoverDate(date);
  }, []);

  const renderDay = (day: Date | null, dayIndex: number) => {
    if (day === null) {
      return null; // Skip null days
    }

    const disabled =
      (disableDatesBefore && isDateBefore(day, disableDatesBefore)) ||
      (disableDatesAfter && isDateAfter(day, disableDatesAfter)) ||
      disableSpecificDates?.some(date => isSameDate(date, day));

    const firstInRange = rangeEnabled && selected && isDateStart(day, selected);
    const lastInRange =
      rangeEnabled &&
      selected &&
      ((!isSameDate(selected.start, selected.end) && isDateEnd(day, selected)) ||
        (hoverDate !== null &&
          !firstInRange &&
          isSameDate(day, hoverDate) &&
          isSameDate(selected.start, selected.end) &&
          isDateAfter(hoverDate, selected.start)));

    const inHoveringRange =
      rangeEnabled &&
      selected &&
      hoverDate !== null &&
      isSameDate(selected.start, selected.end) &&
      day > selected.start &&
      day < hoverDate;

    const isHoverDate =
      rangeEnabled &&
      hoverDate !== null &&
      selected &&
      isDateAfter(day, selected.start) &&
      !!isSameDate(selected.start, selected.end) &&
      isSameDate(day, hoverDate);

    const firstOfMonth = new Date(year, month, 1);
    const lastOfMonth = new Date(year, month + 1, 0);

    const unavailable = isDateBefore(day, firstOfMonth) || isDateAfter(day, lastOfMonth);

    return (
      <Day
        key={dayIndex}
        day={day}
        selected={selected && isDateSelected(day, selected)}
        inRange={rangeEnabled && selected && isDateInRange(day, selected)}
        inHoveringRange={inHoveringRange}
        isHoverDate={isHoverDate}
        firstInRange={firstInRange}
        lastInRange={lastInRange}
        lastOfMonth={lastOfMonth}
        disabled={disabled}
        unavailable={unavailable}
        onClick={dayClickHandler}
        onHover={hoverHandler}
        data-analytics-id="pp_datepicker_select_day"
      />
    );
  };

  const weekRows = weeks.map((week, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <Row key={index}>{week.map(renderDay)}</Row>
  ));

  return (
    <>
      <Row>{daysOfWeek}</Row>
      {weekRows}
    </>
  );
};
